/**
 * 品牌配置加载工具
 * 利用构建时常量实现 tree-shaking，确保只打包当前品牌的代码
 *
 * @example
 * ```ts
 * import { loadBrandConfig } from '@/utils/loadBrandConfig'
 *
 * const config = loadBrandConfig({
 *   rocnovel: rocnovelConfig,
 *   oceread: ocereadConfig,
 *   ficmoon: ficmoonConfig,
 * })
 * ```
 */

type BrandName = 'rocnovel' | 'oceread' | 'talewave' | 'ficmoon' | 'novelarc' | 'storyella' | 'aurenovel' | 'florastory' | 'silktale' | 'pearla' | 'fictora' | 'marinovel' | 'flipnovel' | 'moonpage' | 'storlyn' | 'novaris' | 'novelune' | 'novelyn' | 'novelmello'
type BrandValueMap<T> = Partial<Record<BrandName, T>>

// 声明构建时注入的品牌常量
declare const __BRAND__: BrandName

function pickFirstDefined<T>(configs: BrandValueMap<T>): T {
  const fallback = Object.values(configs).find((item): item is T => item !== undefined)
  if (fallback === undefined)
    throw new Error(`No brand config found for ${__BRAND__}`)

  return fallback
}

function resolveBrandValue<T>(configs: BrandValueMap<T>): T {
  if (__BRAND__ === 'rocnovel')
    return configs.rocnovel ?? configs.novelarc ?? configs.oceread ?? pickFirstDefined(configs)
  if (__BRAND__ === 'oceread')
    return configs.oceread ?? configs.rocnovel ?? pickFirstDefined(configs)
  if (__BRAND__ === 'ficmoon')
    return configs.ficmoon ?? configs.novelarc ?? configs.rocnovel ?? pickFirstDefined(configs)
  if (__BRAND__ === 'storyella')
    return configs.storyella ?? configs.novelarc ?? configs.rocnovel ?? pickFirstDefined(configs)
  if (__BRAND__ === 'aurenovel')
    return configs.aurenovel ?? configs.storyella ?? configs.novelarc ?? configs.rocnovel ?? pickFirstDefined(configs)
  if (__BRAND__ === 'florastory')
    return configs.florastory ?? configs.storyella ?? configs.novelarc ?? configs.rocnovel ?? pickFirstDefined(configs)
  if (__BRAND__ === 'silktale')
    return configs.silktale ?? configs.novelarc ?? configs.rocnovel ?? pickFirstDefined(configs)
  if (__BRAND__ === 'pearla')
    return configs.pearla ?? configs.novelarc ?? configs.rocnovel ?? pickFirstDefined(configs)
  if (__BRAND__ === 'fictora')
    return configs.fictora ?? configs.novelarc ?? configs.rocnovel ?? pickFirstDefined(configs)
  if (__BRAND__ === 'marinovel')
    return configs.marinovel ?? configs.novelarc ?? configs.rocnovel ?? pickFirstDefined(configs)
  if (__BRAND__ === 'talewave')
    return configs.talewave ?? configs.oceread ?? configs.rocnovel ?? pickFirstDefined(configs)
  if (__BRAND__ === 'moonpage')
    return configs.moonpage ?? configs.novelune ?? configs.flipnovel ?? configs.storlyn ?? pickFirstDefined(configs)
  if (__BRAND__ === 'novelune')
    return configs.novelune ?? configs.moonpage ?? configs.flipnovel ?? configs.storlyn ?? pickFirstDefined(configs)
  if (__BRAND__ === 'storlyn')
    return configs.storlyn ?? configs.flipnovel ?? configs.moonpage ?? pickFirstDefined(configs)
  if (__BRAND__ === 'novaris')
    return configs.novaris ?? configs.storlyn ?? configs.flipnovel ?? configs.moonpage ?? pickFirstDefined(configs)
  if (__BRAND__ === 'novelyn')
    return configs.novelyn ?? configs.rocnovel ?? configs.novelarc ?? pickFirstDefined(configs)
  // NovelMello：全显式注册，不回退到任何其他品牌。
  // 取不到就是 undefined —— 绝不落到 pickFirstDefined 或别的品牌值，
  // 让"漏登记"在开发期立刻暴露，而不是静默拿到 storlyn/rocnovel 的皮肤。
  // 全部调用点的登记清单见 plans/8/新站NovelMello/01-brand-config.md 第四节。
  if (__BRAND__ === 'novelmello') {
    if (import.meta.env.DEV && configs.novelmello === undefined)
      console.error('[loadBrandConfig] novelmello 未显式注册，请在该调用点的 map 末尾补 novelmello 键', configs)

    return configs.novelmello as T
  }
  if (__BRAND__ === 'flipnovel')
    return configs.flipnovel ?? configs.storlyn ?? configs.moonpage ?? pickFirstDefined(configs)
  return configs.novelarc ?? configs.rocnovel ?? pickFirstDefined(configs)
}

/**
 * 加载品牌配置
 *
 * 构建时会根据 __BRAND__ 常量进行 tree-shaking，
 * 只保留当前品牌的配置，移除其他品牌的配置
 */
export function loadBrandConfig<T>(configs: BrandValueMap<T>): T {
  return resolveBrandValue(configs)
}

/**
 * 加载品牌专属组件
 *
 * 构建时会根据 __BRAND__ 常量进行 tree-shaking，
 * 只保留当前品牌的组件，移除其他品牌的组件
 */
export function loadBrandComponent<T>(components: BrandValueMap<T>): T {
  return resolveBrandValue(components)
}
